/**
 * DataGridTableBody
 *
 * Contains both standard and virtualized table body renderers.
 * Extracted from AdvancedTableView to keep it under 500 lines.
 *
 * - DataGridStandardBody: direct DOM rendering (< 50 rows)
 * - DataGridVirtualBody: @tanstack/react-virtual rendering (50+ rows)
 */

import type { ReactNode } from 'react';
import { flexRender, type Table, type Row } from '@tanstack/react-table';
import type { Virtualizer } from '@tanstack/react-virtual';
import { cn } from '@/lib/utils';
import { PackageOpen } from 'lucide-react';
import { TableBody, TableCell, TableRow } from '@/components/ui/table';
import { DataGridCellRenderer } from './DataGridCellRenderer';
import { DataGridContextMenu } from './DataGridContextMenu';
import { resolveCellVariant } from './dataGridCellResolver';
import { getCommonPinningStyles } from '@/lib/data-table';
import type { UseDataGridReturn, CellSaveState, CellRangePosition } from './types';
import type { DataGridLabels } from './dataGridLabels';

// ---------------------------------------------------------------------------
// Range perimeter border helper — inset box-shadow, zero layout shift
// ---------------------------------------------------------------------------

const SKY_500 = '#0ea5e9'; // Tailwind sky-500

function buildRangeShadow(pos: CellRangePosition): string {
  const parts: string[] = [];
  if (pos.top) parts.push(`inset 0 2px 0 0 ${SKY_500}`);
  if (pos.bottom) parts.push(`inset 0 -2px 0 0 ${SKY_500}`);
  if (pos.left) parts.push(`inset 2px 0 0 0 ${SKY_500}`);
  if (pos.right) parts.push(`inset -2px 0 0 0 ${SKY_500}`);
  return parts.join(', ');
}

// ---------------------------------------------------------------------------
// Shared props
// ---------------------------------------------------------------------------

export interface DataGridBodyProps<T> {
  table: Table<T>;
  dataGrid: UseDataGridReturn;
  isEditMode: boolean;
  editableFields: Set<string>;
  fieldTypeMap: Map<string, string | undefined>;
  selectOptions?: Record<string, { label: string; value: string }[]>;
  onRowClick?: (row: T) => void;
  columnCount: number;
  emptyTitle: string;
  emptyDescription: string;
  labels?: Partial<DataGridLabels>;
}

// Stable empty set — avoids allocating per row render
const EMPTY_EDITABLE = new Set<string>();

// ---------------------------------------------------------------------------
// Shared row renderer
// ---------------------------------------------------------------------------

function renderRow<T>(
  row: Row<T>,
  props: DataGridBodyProps<T>,
  extraProps?: { ref?: (el: HTMLTableRowElement | null) => void; style?: React.CSSProperties },
) {
  const { dataGrid, isEditMode, editableFields, fieldTypeMap, selectOptions, onRowClick, labels } = props;

  return (
    <TableRow
      key={row.id}
      ref={extraProps?.ref}
      style={extraProps?.style}
      data-index={row.index}
      data-state={row.getIsSelected() && 'selected'}
      className={cn(
        onRowClick && !isEditMode && 'cursor-pointer hover:bg-accent/50 transition-colors',
        row.getIsSelected() && 'bg-primary/5',
      )}
      onClick={() => {
        if (!isEditMode) onRowClick?.(row.original);
      }}
    >
      {row.getVisibleCells().map((cell) => {
        const colId = cell.column.id;
        const rowIdx = row.index;
        const editable = isEditMode && dataGrid.isCellEditable(colId);
        const active = isEditMode && dataGrid.isCellActive(rowIdx, colId);
        const readonlyInEdit = isEditMode && !editable && colId !== '__select';
        const rowName = (row.original as { name?: string }).name ?? '';
        const rowSaveState = isEditMode && colId === '__select'
          ? dataGrid.getRowSaveState(rowName)
          : undefined;
        const cellSaveState: CellSaveState | undefined = active
          ? dataGrid.getCellSaveState(rowName, colId)
          : undefined;

        // Range selection highlight (Plan 124)
        const inRange = isEditMode && dataGrid.isCellInRange(rowIdx, colId);
        const rangePos = inRange ? dataGrid.getCellRangePosition(rowIdx, colId) : null;
        const rangeShadow = rangePos && !active ? buildRangeShadow(rangePos) : undefined;

        const variant = active && editable
          ? resolveCellVariant(fieldTypeMap.get(colId), colId, editableFields ?? EMPTY_EDITABLE)
          : undefined;

        const pinned = cell.column.getIsPinned();

        return (
          <TableCell
            key={cell.id}
            data-row-index={colId !== '__select' ? rowIdx : undefined}
            data-column-id={colId !== '__select' ? colId : undefined}
            style={{
              ...getCommonPinningStyles({ column: cell.column }),
              ...(rangeShadow ? { boxShadow: rangeShadow } : undefined),
            }}
            data-pinned={pinned || undefined}
            className={cn(
              pinned && 'bg-card',
              isEditMode && colId !== '__select' && 'border-r border-border/30',
              editable && !active && 'cursor-cell hover:ring-1 hover:ring-border/50',
              active && 'ring-2 ring-sky-500 bg-background shadow-sm relative z-10',
              readonlyInEdit && 'bg-muted/30 cursor-default',
              // Range tint: only when in range AND not the active cell
              inRange && !active && 'bg-sky-500/[0.08]',
              cellSaveState?.status === 'error' && 'ring-2 ring-red-500',
              cellSaveState?.conflict && 'ring-2 ring-yellow-500',
            )}
            title={cellSaveState?.conflict ? (labels?.dgConflictTooltip ?? 'Modified by another user') : undefined}
            onMouseDown={isEditMode && colId !== '__select' ? (e) => {
              dataGrid.handleCellMouseDown({ rowIndex: rowIdx, columnId: colId }, e);
            } : undefined}
            onClick={
              editable
                ? (e) => { e.stopPropagation(); }
                : readonlyInEdit
                  ? () => onRowClick?.(row.original)
                  : undefined
            }
          >
            {rowSaveState && rowSaveState !== 'idle' && (
              <span className={cn(
                'absolute left-0.5 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full',
                rowSaveState === 'saving' && 'bg-sky-400 animate-pulse',
                rowSaveState === 'error' && 'bg-red-400',
              )} />
            )}
            {(() => {
              const cellContent = active && editable && variant && variant !== 'readonly' ? (
                <DataGridCellRenderer
                  variant={variant}
                  value={row.getValue(colId)}
                  onCommit={dataGrid.commitEdit}
                  onCancel={dataGrid.cancelEdit}
                  onStartEditing={dataGrid.startEditing}
                  saveState={cellSaveState ?? { status: 'idle' }}
                  fieldname={colId}
                  options={selectOptions?.[colId]}
                />
              ) : flexRender(cell.column.columnDef.cell, cell.getContext());

              if (!isEditMode || colId === '__select') return cellContent;

              return (
                <DataGridContextMenu
                  onCopy={dataGrid.copy}
                  onPaste={dataGrid.paste}
                  onCut={dataGrid.cut}
                  onClear={() => { if (editable) dataGrid.commitEdit(''); }}
                  onViewDetail={() => onRowClick?.(row.original)}
                  onOpenChange={(open) => {
                    if (open && editable) dataGrid.selectCell({ rowIndex: rowIdx, columnId: colId });
                  }}
                  isEditable={editable}
                  labels={labels}
                  rangeSize={dataGrid.rangeSize}
                >
                  <div className="w-full">{cellContent}</div>
                </DataGridContextMenu>
              );
            })()}
          </TableCell>
        );
      })}
    </TableRow>
  );
}

// ---------------------------------------------------------------------------
// Standard body (no virtualization)
// ---------------------------------------------------------------------------

export function DataGridStandardBody<T>(props: DataGridBodyProps<T>) {
  const { table, columnCount, emptyTitle, emptyDescription } = props;
  const rows = table.getRowModel().rows;

  return (
    <TableBody>
      {rows.length > 0 ? (
        rows.map((row) => renderRow(row, props))
      ) : (
        <TableRow>
          <TableCell colSpan={columnCount} className="h-48 text-center">
            <div className="flex flex-col items-center gap-2 text-muted-foreground">
              <PackageOpen className="h-10 w-10 opacity-40" />
              <p className="text-sm font-medium">{emptyTitle}</p>
              <p className="text-xs">{emptyDescription}</p>
            </div>
          </TableCell>
        </TableRow>
      )}
    </TableBody>
  );
}

// ---------------------------------------------------------------------------
// Virtual body (@tanstack/react-virtual)
// ---------------------------------------------------------------------------

interface DataGridVirtualBodyProps<T> extends DataGridBodyProps<T> {
  virtualizer: Virtualizer<HTMLDivElement, Element>;
}

export function DataGridVirtualBody<T>(props: DataGridVirtualBodyProps<T>) {
  const { table, virtualizer, columnCount, emptyTitle, emptyDescription } = props;
  const rows = table.getRowModel().rows;

  if (rows.length === 0) {
    return (
      <TableBody>
        <TableRow>
          <TableCell colSpan={columnCount} className="h-48 text-center">
            <div className="flex flex-col items-center gap-2 text-muted-foreground">
              <PackageOpen className="h-10 w-10 opacity-40" />
              <p className="text-sm font-medium">{emptyTitle}</p>
              <p className="text-xs">{emptyDescription}</p>
            </div>
          </TableCell>
        </TableRow>
      </TableBody>
    );
  }

  const virtualRows = virtualizer.getVirtualItems();

  return (
    <TableBody
      style={{
        height: `${virtualizer.getTotalSize()}px`,
        position: 'relative',
      }}
    >
      {virtualRows.map((virtualRow) => {
        const row = rows[virtualRow.index];
        if (!row) return null;
        return renderRow(row, props, {
          ref: (el) => virtualizer.measureElement(el),
          style: {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            transform: `translateY(${virtualRow.start}px)`,
          },
        }) as ReactNode;
      })}
    </TableBody>
  );
}
